/**
 * UnifiedPaymentModal.js
 *
 * Main orchestration layer for the payment modal.
 * Coordinates between state, rendering, event handling, and payment processing modules.
 */

import { PaymentState } from './PaymentState.js'
import { PaymentTimer } from './PaymentTimer.js'
import { PaymentRenderers } from './PaymentRenderers.js'
import { PaymentEventHandlers } from './PaymentEventHandlers.js'
import { PaymentProcessing } from './PaymentProcessing.js'

export class UnifiedPaymentModal {
  constructor(paymentService = null, options = {}) {
    this.isVisible = false
    this.currentOrder = null
    this.currentPaymentMethod = null
    this.onClose = null
    this.paymentService = paymentService
    this.paymentUnsubscribe = null
    this.currentPayment = null
    this.beforeOrder = !!options.beforeOrder
    this.createDraftOrder = options.createDraftOrder || null
    this.i18n = options.i18n || null
    this.displayItems = []
    this.currentBlikCode = ''

    // Initialize modules
    this.state = new PaymentState(this.i18n)
    this.timer = new PaymentTimer(15 * 60, () => this.handleSessionTimeout())
    this.renderers = new PaymentRenderers(
      (key, fallback) => this.t(key, fallback),
      this.state
    )
    this.eventHandlers = new PaymentEventHandlers(this)
    this.processing = new PaymentProcessing(this)
  }

  /**
   * Show the payment modal
   */
  show(order, onClose) {
    console.log('[UnifiedPaymentModal] Show method called:', { isVisible: this.isVisible, order });

    if (this.isVisible) {
      console.log('[UnifiedPaymentModal] Modal already visible, returning');
      return
    }

    this.currentOrder = order
    if (this.currentOrder && !this.currentOrder.currency) {
      this.currentOrder.currency = order?.currency || this.i18n?.config?.settings?.currency || 'PLN'
    }
    this.displayItems = this.state.prepareDisplayItems(order, this.beforeOrder)
    if (this.displayItems.length > 0) {
      this.currentOrder.currency = this.displayItems[0].currency || this.currentOrder.currency
    }
    this.onClose = onClose
    this.isVisible = true

    console.log('[UnifiedPaymentModal] Rendering modal...');
    this.render()
    this.eventHandlers.bindEvents()
    this.timer.start()

    document.body.style.overflow = 'hidden'
    console.log('[UnifiedPaymentModal] Modal should now be visible');
  }

  /**
   * Hide the payment modal
   */
  hide() {
    this.isVisible = false
    const modal = document.getElementById('unified-payment-modal')
    if (modal) {
      modal.remove()
    }

    this.timer.stop()
    this.processing.stopListening()
    document.body.style.overflow = ''

    if (this.onClose) {
      this.onClose()
    }
  }

  /**
   * Render the modal
   */
  render() {
    const orderItemsHtml = this.renderOrderItems()
    const paymentOptionsHtml = this.renderers.renderPaymentOptions(this.currentPaymentMethod)
    const modalHtml = this.renderers.renderModal(orderItemsHtml, paymentOptionsHtml)

    document.body.insertAdjacentHTML('beforeend', modalHtml)
  }

  /**
   * Render order items section
   */
  renderOrderItems() {
    const sourceItems = this.beforeOrder ? (this.currentOrder?.items || []) : this.displayItems
    const totalGross = this.state.getOrderTotalGross(this.currentOrder, this.displayItems, this.beforeOrder)
    const currency = this.currentOrder?.currency || this.displayItems?.[0]?.currency || 'PLN'

    return this.renderers.renderOrderItems(
      sourceItems,
      this.beforeOrder,
      (item) => this.state.getItemImage(item),
      (item) => this.state.getItemModifiers(item, (key, fallback) => this.t(key, fallback)),
      (amount) => this.state.formatCurrency(amount, currency),
      (item) => this.state.getItemTotalPrice(item)
    ) + `
      <div class="order-calculation">
        <div class="total-row">
          <div class="total-label">${this.t('order.totalLabel', 'Total')}</div>
          <div class="total-amount">${this.state.formatCurrency(totalGross, currency)}</div>
        </div>
      </div>
      <span class="order-summary-divider"></span>
    `
  }

  /**
   * Refresh payment view
   */
  refreshPaymentView() {
    const paymentContent = document.querySelector('#unified-payment-modal #payment-content')
    if (paymentContent) {
      paymentContent.innerHTML = this.renderers.renderPaymentOptions(this.currentPaymentMethod)
      this.eventHandlers.bindEvents()
    }
  }

  /**
   * Select payment method
   */
  async selectPaymentMethod(method) {
    console.log('[UnifiedPaymentModal] Selected payment method:', method)
    this.currentPaymentMethod = (method || '').toLowerCase()

    if (method === 'STAFF_PAYMENT') {
      this.processing.notifyStaff('red')
      try {
        document.dispatchEvent(new CustomEvent('showStaffCallModal'))
      } catch (error) {
        console.warn('[UnifiedPaymentModal] Failed to trigger staff call modal', error)
      }
    }

    this.refreshPaymentView()

    if ((!this.currentOrder || !this.currentOrder.id) && typeof this.createDraftOrder === 'function') {
      try {
        this.currentOrder = await this.createDraftOrder(this.currentPaymentMethod)
        this.displayItems = this.state.prepareDisplayItems(this.currentOrder, this.beforeOrder)
        if (this.displayItems.length > 0) {
          this.currentOrder.currency = this.displayItems[0].currency || this.currentOrder.currency
        }
        console.log('[UnifiedPaymentModal] Draft order created:', this.currentOrder?.id)
        const itemsRoot = document.querySelector('#unified-payment-modal .order-items')
        const totalEl = document.querySelector('#unified-payment-modal .total-amount')
        const currency = this.currentOrder?.currency || 'PLN'
        if (itemsRoot) itemsRoot.innerHTML = this.renderOrderItems()
        if (totalEl) {
          const total = this.state.getOrderTotalGross(this.currentOrder, this.displayItems, this.beforeOrder)
          totalEl.textContent = this.state.formatCurrency(total, currency)
        }
      } catch (err) {
        console.error('[UnifiedPaymentModal] Failed to create draft order:', err)
        return
      }
    }

    if (this.currentPaymentMethod === 'qr_code') {
      await this.processing.startQrPaymentFlow()
    }
  }

  /**
   * Update from cart changes
   */
  updateFromCart(cartItems, totals, currency) {
    this.currentOrder = this.currentOrder || {}
    this.currentOrder.items = cartItems
    this.currentOrder.total = totals?.total ?? totals
    if (currency) this.currentOrder.currency = currency
    this.displayItems = this.state.prepareDisplayItems(this.currentOrder, this.beforeOrder)
    if (this.displayItems.length > 0) {
      this.currentOrder.currency = this.displayItems[0].currency || this.currentOrder.currency
    }

    const itemsRoot = document.querySelector('#unified-payment-modal .order-items')
    const totalEl = document.querySelector('#unified-payment-modal .total-amount')
    const totalGross = this.state.getOrderTotalGross(this.currentOrder, this.displayItems, this.beforeOrder)
    const curr = this.currentOrder?.currency || 'PLN'

    if (itemsRoot) itemsRoot.innerHTML = this.renderOrderItems()
    if (totalEl) totalEl.textContent = this.state.formatCurrency(totalGross, curr)
    if (this.beforeOrder) {
      this.eventHandlers.bindQuantityControls()
    }
  }

  /**
   * Process BLIK payment
   */
  async processBLIKPayment() {
    await this.processing.processBLIKPayment()
  }

  /**
   * Process fallback payment
   */
  processFallbackPayment() {
    this.processing.processFallbackPayment()
  }

  /**
   * Handle session timeout
   */
  handleSessionTimeout() {
    console.log('[UnifiedPaymentModal] Session timeout')

    const event = new CustomEvent('paymentSessionTimeout', {
      detail: {
        order: this.currentOrder,
        timestamp: Date.now()
      }
    })
    document.dispatchEvent(event)

    this.hide()
  }

  /**
   * Translation helper
   */
  t(key, fallback) {
    if (!this.i18n || typeof this.i18n.t !== 'function') {
      return fallback
    }
    const translation = this.i18n.t(key)
    return translation === key ? fallback : translation
  }
}
